import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useTemplateContext } from '../../../context/templateContext'
import { useComparableField } from '../../../context/useComparableField'
import { useIsErrorForField } from '../../../hooks/useIsErrorForField'
import type { ConfigurationTemplateFunctionValue } from '../../../types/templateModel'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { stringifyComparableValue } from '../../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../../diffIndicators/ValueSetupDiffIndicators'
import { TemplateDocumentationEntry } from '../../documentationEntry/TemplateDocumentationEntry'
import { TemplateFunctionDocumentation } from '../../templateFieldDocumentation/TemplateFunctionDocumentation'
import { FunctionValue } from '../../templateFieldsTable/FunctionValue'

type Props = {
  data: ConfigurationTemplateFunctionValue
}

export const TemplateFunctionContent = ({ data }: Props) => {
  const translate = useTranslate()
  const { errors } = useOperationsContext()
  const { isDraftTemplate } = useTemplateContext()
  const isErrorForField = useIsErrorForField()

  const functionValueErrors = useMemo(
    () => errors.filter(({ error }) => isErrorForField(data, error)),
    [data, errors, isErrorForField],
  )

  const comparableField =
    useComparableField<ConfigurationTemplateFunctionValue>(data.path)

  const valueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<ConfigurationTemplateFunctionValue>) =>
      comparable?.function ? stringifyComparableValue(comparable.function) : '',
    [],
  )

  return (
    <>
      {data.isParentNotRelevant && (
        <Alert
          severity="info"
          title={translate(
            'templateViewer.fieldDetailsModal.field.notRelevant.info',
          )}
          className="col-span-2"
        />
      )}
      <TemplateFunctionDocumentation
        areFieldsShownAsEditable={isDraftTemplate}
        data={data}
      />
      <TemplateDocumentationEntry
        isShowingErrors
        title={translate('templateViewer.fieldDetailsModal.field.defaultValue')}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={valueFromComparable}
            compareFilter="defaultValue"
          />
        }
        errors={functionValueErrors}
        value={<FunctionValue data={data} isExpanded />}
        origin={data.origin}
        path={data.path}
      />
    </>
  )
}
